import { ZodError } from "zod";
import { ApiClientError } from "@/application/errors/ApiClientError";
import { ContractViolationError } from "@/application/errors/ContractViolationError";
import { extractFilenameFromHeader } from "./fileDownload";

interface SdkResult<T> {
  data?: T;
  error?: unknown;
  request?: Request;
  response?: Response;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function toApiClientError(error: unknown, request?: Request, response?: Response): unknown {
  const endpoint = request ? new URL(request.url).pathname : undefined;
  const method = request?.method;

  // Contract violation from the zod response validator — not an HTTP failure.
  // The monitoring layer reports it as spec drift.
  if (error instanceof ZodError) return new ContractViolationError(error, endpoint, method);

  // Caller-driven cancellation (e.g. TanStack Query) must stay an AbortError.
  if (error instanceof DOMException && error.name === "AbortError") return error;

  const traceId = response?.headers.get("X-Trace-ID") ?? undefined;
  const requestId = response?.headers.get("X-Request-ID") ?? undefined;

  if (error instanceof DOMException && error.name === "TimeoutError") {
    return new ApiClientError({
      message: "Request timeout",
      status: 408,
      code: "TIMEOUT",
      endpoint,
      method,
    });
  }

  if (response) {
    const body = isRecord(error) ? error : undefined;
    const message =
      typeof body?.message === "string"
        ? body.message
        : `HTTP ${response.status}: ${response.statusText}`;
    const code =
      (typeof body?.code === "string" && body.code) ||
      (typeof body?.error === "string" && body.error) ||
      undefined;
    const details = isRecord(body?.details) ? body.details : undefined;
    return new ApiClientError({
      message,
      status: response.status,
      code,
      details,
      endpoint,
      method,
      traceId,
      requestId,
    });
  }

  return new ApiClientError({
    message: "Network error or unexpected response",
    status: 0,
    code: "NETWORK_ERROR",
    details: { originalError: error instanceof Error ? error.message : String(error) },
    endpoint,
    method,
  });
}

/**
 * Await a generated SDK call and either return its data or throw the same
 * error types the application layer already understands (`ApiClientError`,
 * `ZodError` for contract drift, `AbortError` for cancellations).
 */
export async function unwrap<T>(result: Promise<SdkResult<T>>): Promise<T> {
  const { data, error, request, response } = await result;
  if (error !== undefined) {
    throw toApiClientError(error, request, response);
  }
  return data as T;
}

type BlobCall<O> = (options: O & { parseAs: "blob" }) => Promise<SdkResult<unknown>>;

async function requestBlob<O extends object>(call: BlobCall<O>, options: O) {
  const { data, error, request, response } = await call({ ...options, parseAs: "blob" });
  if (error !== undefined || !(data instanceof Blob)) {
    throw toApiClientError(error ?? new Error("Expected Blob response"), request, response);
  }
  return { blob: data, response };
}

/**
 * Like {@link unwrap} for binary downloads: issues the SDK call with the body
 * read as a Blob whatever its Content-Type, and returns it together with the
 * filename announced by the backend via Content-Disposition.
 */
export async function unwrapBlobWithFilename<O extends object>(
  call: BlobCall<O>,
  options: O,
  fallbackFilename: string,
): Promise<{ blob: Blob; filename: string }> {
  const { blob, response } = await requestBlob(call, options);
  const contentDisposition = response?.headers.get("Content-Disposition") ?? null;
  return { blob, filename: extractFilenameFromHeader(contentDisposition, fallbackFilename) };
}

/**
 * Like {@link unwrap} for binary endpoints: issues the SDK call with the body
 * read as a Blob whatever its Content-Type.
 */
export async function unwrapBlob<O extends object>(call: BlobCall<O>, options: O): Promise<Blob> {
  const { blob } = await requestBlob(call, options);
  return blob;
}
